import { z } from 'zod'

/**
 * 有料プランへのアップグレード申請（人手対応の問い合わせフォーム）。
 *
 * - サインアップ済みユーザー（FREE / 暫定→FREE migrate 済み）が提出可能
 * - 暫定テナント (EPHEMERAL) は提出不可（先に正規テナントへ migrate する）
 * - 提出時に管理者にメール通知 + 管理画面の pending バッジが増える
 * - 管理者が status を進めて、最終的に PATCH /admin/tenants/:id/plan で実プラン変更
 *
 * 関連: docs/business-requirements.md §6 / docs/system-requirements.md §4 (拡張)
 */

export const PlanUpgradeRequestStatusSchema = z.enum([
  'PENDING',
  'CONTACTED',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
])
export type PlanUpgradeRequestStatus = z.infer<typeof PlanUpgradeRequestStatusSchema>

// ----- Resource shape -----

export const PlanUpgradeRequestSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  /** 申請者の public.users.id */
  requestedBy: z.string().uuid(),
  contactEmail: z.string().email(),
  contactName: z.string().nullable(),
  companyName: z.string().nullable(),
  phone: z.string().nullable(),
  /** 請求先郵便番号（ハイフン有無問わず） */
  postalCode: z.string().nullable(),
  /** 請求先住所（都道府県+市区町村+番地・建物名を1フィールドに） */
  billingAddress: z.string().nullable(),
  /** 請求書送付先メアド（contactEmail と分けたい場合のみ） */
  billingEmail: z.string().nullable(),
  desiredAddonStorageTb: z.number().int().nonnegative(),
  message: z.string().nullable(),
  status: PlanUpgradeRequestStatusSchema,
  reviewerNote: z.string().nullable(),
  reviewedBy: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type PlanUpgradeRequest = z.infer<typeof PlanUpgradeRequestSchema>

/** 管理画面表示用に申請者・テナント情報を併記したリッチ版 */
// 注: tenant / requester は通常存在するが、cascade を抜けて消えた異常データに耐えるため null 許容にする
export const AdminPlanUpgradeRequestSchema = PlanUpgradeRequestSchema.extend({
  tenant: z
    .object({
      id: z.string().uuid(),
      name: z.string().nullable(),
      plan: z.enum(['FREE', 'PRO', 'EPHEMERAL']),
      status: z.enum(['ACTIVE', 'SUSPENDED', 'FROZEN']),
    })
    .nullable(),
  requester: z
    .object({
      id: z.string().uuid(),
      email: z.string(),
    })
    .nullable(),
})
export type AdminPlanUpgradeRequest = z.infer<typeof AdminPlanUpgradeRequestSchema>

// ----- Owner: 申請作成 / 更新 -----

const planUpgradeRequestFormFieldsSchema = z.object({
  contactName: z.string().trim().min(1).max(120),
  companyName: z.string().trim().min(1).max(200),
  phone: z.string().trim().min(1).max(50),
  /** 郵便番号（"100-0001" / "1000001" どちらも許容） */
  postalCode: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[0-9-]+$/, '郵便番号は数字とハイフンのみ使えます'),
  /** 請求先住所（都道府県+市区町村+番地・建物名を1フィールドに） */
  billingAddress: z.string().trim().min(1).max(500),
  /** 請求書送付先メアド（contactEmail と異なる場合に入力） */
  billingEmail: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().email().max(255).optional(),
  ),
  desiredAddonStorageTb: z.coerce.number().int().min(0).max(1000).default(0),
  message: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().max(2000).optional(),
  ),
})
export type PlanUpgradeRequestFormFields = z.infer<typeof planUpgradeRequestFormFieldsSchema>

export const CreatePlanUpgradeRequestInputSchema = planUpgradeRequestFormFieldsSchema
export type CreatePlanUpgradeRequestInput = PlanUpgradeRequestFormFields

export const UpdatePlanUpgradeRequestInputSchema = planUpgradeRequestFormFieldsSchema
export type UpdatePlanUpgradeRequestInput = z.infer<typeof UpdatePlanUpgradeRequestInputSchema>

// ----- Owner: 自分の申請一覧 -----

export const MyPlanUpgradeRequestsResultSchema = z.object({
  items: z.array(PlanUpgradeRequestSchema),
})
export type MyPlanUpgradeRequestsResult = z.infer<typeof MyPlanUpgradeRequestsResultSchema>

// ----- Admin: 一覧 -----

export const ListAdminPlanUpgradeRequestsQuerySchema = z.object({
  status: PlanUpgradeRequestStatusSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
})
export type ListAdminPlanUpgradeRequestsQuery = z.infer<
  typeof ListAdminPlanUpgradeRequestsQuerySchema
>

export const ListAdminPlanUpgradeRequestsResultSchema = z.object({
  items: z.array(AdminPlanUpgradeRequestSchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
})
export type ListAdminPlanUpgradeRequestsResult = z.infer<
  typeof ListAdminPlanUpgradeRequestsResultSchema
>

// ----- Admin: ステータス更新 -----

export const UpdateAdminPlanUpgradeRequestInputSchema = z.object({
  status: PlanUpgradeRequestStatusSchema,
  reviewerNote: z.string().trim().max(2000).optional(),
})
export type UpdateAdminPlanUpgradeRequestInput = z.infer<
  typeof UpdateAdminPlanUpgradeRequestInputSchema
>

export const PlanUpgradeRequestIdParamsSchema = z.object({
  id: z.string().uuid(),
})
